import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, FileText, Loader2, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";
import { DIALOG_NAME, useDialogSearchParam } from "../../helpers/useDialogSearchParam";
import SolvTaxLogo from "../general/Logo";
import { getOrCreateSessionId } from "../../lib/triageJobsStorage";
import { validateIntakeFile } from "../../lib/validateIntakeFile";
import { trackActivity } from "../../lib/siteActivity";
import { leadsAPI } from "../../services/api";
import HoneypotField from "../security/HoneypotField";
import TurnstileWidget, { isTurnstileRequired } from "../security/TurnstileWidget";

const ASSET_VALUE_OPTIONS = [
  { value: "", label: "Select estimated equipment value (optional)" },
  { value: "under-250k", label: "Under $250,000" },
  { value: "250k-1m", label: "$250,000 – $1,000,000" },
  { value: "1m-5m", label: "$1,000,000 – $5,000,000" },
  { value: "over-5m", label: "Over $5,000,000" },
];

const LABEL_CLASS = "text-sm font-semibold text-slate-800";

const FIELD_CLASS =
  "mt-1.5 w-full rounded-none bg-[#dbe1ec]/70 px-4 py-2 md:py-2.5 text-xs md:text-sm text-slate-950 placeholder:text-slate-400 border-0 border-transparent outline-none focus:outline-none focus-visible:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-shadow";

/** @typedef {'idle' | 'submitting' | 'success' | 'error'} SubmitStatus */

export default function WaitlistModal() {
  const [isOpen, removeParam] = useDialogSearchParam(DIALOG_NAME.waitlist);
  const fileInputRef = useRef(null);
  const [sessionId] = useState(() => getOrCreateSessionId());

  useEffect(() => {
    if (!isOpen) return;
    trackActivity({ event: "waitlist_opened", path: window.location.pathname });
  }, [isOpen]);

  const [form, setForm] = useState({
    fullName: "",
    businessEmail: "",
    companyName: "",
    estimatedAssetValue: "",
    website_url_hp: "",
  });
  /** @type {[{ fileName: string, file: File|null }, React.Dispatch<React.SetStateAction<{ fileName: string, file: File|null }>>]} */
  const [noticeFile, setNoticeFile] = useState({ fileName: "", file: null });
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  /** @type {[SubmitStatus, React.Dispatch<React.SetStateAction<SubmitStatus>>]} */
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitError, setSubmitError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(undefined);
  const turnstileRequired = isTurnstileRequired();

  const resetForm = useCallback(() => {
    setForm({
      fullName: "",
      businessEmail: "",
      companyName: "",
      estimatedAssetValue: "",
      website_url_hp: "",
    });
    setNoticeFile({ fileName: "", file: null });
    setUploadError(null);
    setDragActive(false);
    setSubmitStatus("idle");
    setSubmitError(null);
    setTurnstileToken(undefined);
  }, []);

  const handleClose = () => {
    removeParam();
    resetForm();
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleNoticeSelect = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    applyNoticeFile(file);
  };

  const applyNoticeFile = (file) => {
    const result = validateIntakeFile(file, "assessment");
    if (!result.valid) {
      setUploadError(result.error);
      setNoticeFile({ fileName: "", file: null });
      return;
    }

    setUploadError(null);
    setNoticeFile({ fileName: file.name, file });
    trackActivity({
      event: "document_uploaded",
      path: window.location.pathname,
      label: "waitlist-notice",
      meta: { fileName: String(file.name || "").slice(0, 120) },
    });
  };

  const clearNoticeFile = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setNoticeFile({ fileName: "", file: null });
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const preventBrowserFileOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (submitStatus === "submitting") return;
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (submitStatus === "submitting") return;

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    applyNoticeFile(file);
  };

  const canSubmit =
    form.fullName.trim() &&
    form.businessEmail.trim() &&
    form.companyName.trim() &&
    (!turnstileRequired || Boolean(turnstileToken)) &&
    submitStatus !== "submitting" &&
    submitStatus !== "success";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitStatus("submitting");
    setSubmitError(null);
    try {
      await leadsAPI.waitlist({
        sessionId,
        fullName: form.fullName.trim(),
        businessEmail: form.businessEmail.trim(),
        companyName: form.companyName.trim(),
        estimatedAssetValue: form.estimatedAssetValue || undefined,
        noticeFile: noticeFile.file || undefined,
        website_url_hp: form.website_url_hp,
        "cf-turnstile-response": turnstileToken,
      });
      setSubmitStatus("success");
    } catch (err) {
      setSubmitStatus("error");
      setSubmitError(err?.message ?? "Submission failed. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-modal-title"
      onClick={handleBackdropClick}
      onDragEnter={preventBrowserFileOpen}
      onDragOver={preventBrowserFileOpen}
      onDrop={preventBrowserFileOpen}
      onKeyDown={(event) => {
        if (event.key === "Escape") handleClose();
      }}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl text-slate-900 flex flex-col max-h-[92vh] md:max-h-[90vh] overflow-hidden">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 md:top-6 md:right-6 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500/40 z-10"
          aria-label="Close waitlist modal"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="overflow-y-auto p-6 md:p-8 flex-1 min-h-0 scrollbar-thin">
          {submitStatus === "success" ? (
            <div className="flex flex-col items-center justify-center py-6 text-center space-y-5">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 border border-emerald-100 shadow-sm mb-1">
                <CheckCircle2 size={36} />
              </div>

              <div className="flex items-center justify-center">
                <SolvTaxLogo color="#0f172a" linkable={false} compact />
              </div>

              <div className="space-y-2 max-w-sm">
                <h4
                  id="waitlist-modal-title"
                  className="text-xl md:text-2xl font-extrabold text-slate-950 tracking-tight"
                >
                  Application Received
                </h4>
                <p className="text-sm md:text-base text-slate-500 leading-relaxed mt-2">
                  Your application has been recorded. Our corporate compliance team will review your
                  details and reach out regarding your diagnostic tax savings report shortly.
                </p>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="w-full max-w-xs py-3 rounded-full bg-slate-900 hover:bg-slate-950 text-white text-sm font-semibold transition-all shadow-md mt-4"
              >
                Close Window
              </button>
            </div>
          ) : (
            <div className="space-y-4 md:space-y-5">
              <div className="text-center pt-2">
                <div className="flex items-center justify-center">
                  <SolvTaxLogo color="#0f172a" linkable={false} compact />
                </div>
                <h3
                  id="waitlist-modal-title"
                  className="text-xl md:text-2xl font-bold text-slate-950 tracking-tight mt-1"
                >
                  Request Access
                </h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto mt-2 leading-relaxed">
                  Create a secure corporate portal account. We onboard CFOs and controllers on a
                  rolling basis to maintain high-touch Business Personal Property (BPP) audit
                  quality.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="relative space-y-3.5 md:space-y-5">
                <HoneypotField
                  id="waitlist_website_url_hp"
                  value={form.website_url_hp}
                  onChange={(e) => setForm((prev) => ({ ...prev, website_url_hp: e.target.value }))}
                />
                <label className="block text-left">
                  <span className={LABEL_CLASS}>
                    Full name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => setForm((prev) => ({ ...prev, fullName: e.target.value }))}
                    placeholder="Jordan Lee"
                    className={FIELD_CLASS}
                    required
                    disabled={submitStatus === "submitting"}
                  />
                </label>

                <label className="block text-left">
                  <span className={LABEL_CLASS}>
                    Business email <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="email"
                    value={form.businessEmail}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, businessEmail: e.target.value }))
                    }
                    placeholder="you@company.com"
                    className={FIELD_CLASS}
                    required
                    disabled={submitStatus === "submitting"}
                  />
                </label>

                <label className="block text-left">
                  <span className={LABEL_CLASS}>
                    Company name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))}
                    placeholder="Acme Manufacturing LLC"
                    className={FIELD_CLASS}
                    required
                    disabled={submitStatus === "submitting"}
                  />
                </label>

                <label className="block text-left">
                  <span className={LABEL_CLASS}>Estimated asset pool / equipment value</span>
                  <div className="relative mt-1.5">
                    <select
                      value={form.estimatedAssetValue}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, estimatedAssetValue: e.target.value }))
                      }
                      disabled={submitStatus === "submitting"}
                      className={`${FIELD_CLASS} mt-0 appearance-none pr-10 leading-none text-slate-800`}
                    >
                      {ASSET_VALUE_OPTIONS.map((option) => (
                        <option key={option.value || "default"} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={18}
                      aria-hidden
                      className="pointer-events-none absolute right-3 top-1/2 block -translate-y-1/2 text-slate-500"
                    />
                  </div>
                </label>

                <div className="text-left">
                  <p className={`${LABEL_CLASS} mb-2`}>County tax notice (optional)</p>
                  {noticeFile.file ? (
                    <div className="relative flex items-center justify-between rounded-xl bg-emerald-600 text-white p-3.5 border border-emerald-700 shadow-md transition-all duration-200">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/50 text-white shrink-0">
                          <CheckCircle2 size={18} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate text-white max-w-[200px] md:max-w-[260px]">
                            {noticeFile.fileName}
                          </p>
                          <p className="text-[10px] text-emerald-100">
                            Ready to analyze ({(noticeFile.file.size / 1024 / 1024).toFixed(2)} MB)
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearNoticeFile}
                        disabled={submitStatus === "submitting"}
                        className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-full bg-emerald-700/50 hover:bg-emerald-700 text-white hover:text-red-200 text-[11px] font-semibold transition-all shadow-inner focus:outline-none focus:ring-2 focus:ring-white/40 shrink-0 disabled:opacity-50"
                      >
                        <X size={12} />
                        <span>Clear</span>
                      </button>
                    </div>
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                      }}
                      onClick={() => submitStatus !== "submitting" && fileInputRef.current?.click()}
                      onDragEnter={handleDrag}
                      onDragOver={handleDrag}
                      onDragLeave={handleDrag}
                      onDrop={handleDrop}
                      className={[
                        "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-3 md:p-4 text-center cursor-pointer transition-all",
                        dragActive
                          ? "border-cyan-400 bg-cyan-50/70"
                          : "border-slate-200 hover:border-slate-300 bg-slate-50/50",
                        submitStatus === "submitting" ? "cursor-wait opacity-80" : "",
                      ].join(" ")}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        disabled={submitStatus === "submitting"}
                        onChange={handleNoticeSelect}
                      />
                      <Upload size={20} className="text-slate-400" />
                      <p className="mt-2 text-sm text-slate-700">
                        {dragActive ? "Drop PDF to attach" : "Click to upload or drag a PDF here"}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 flex items-center justify-center gap-1">
                        <FileText className="h-3.5 w-3.5" />
                        County tax notice (PDF)
                      </p>
                    </div>
                  )}
                  {uploadError && (
                    <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      {uploadError}
                    </p>
                  )}
                </div>

                {submitStatus === "error" && submitError && (
                  <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <p>{submitError}</p>
                  </div>
                )}

                <TurnstileWidget onTokenChange={setTurnstileToken} />

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-950 text-white font-semibold py-3.5 px-4 shadow-lg shadow-slate-900/10 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {submitStatus === "submitting" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    "Request Early Access"
                  )}
                </button>
                <p className="text-[11px] text-slate-500 text-center mt-4 leading-normal">
                  By continuing, you agree to our{" "}
                  <Link
                    to="/terms"
                    className="text-cyan-500 hover:text-cyan-600 underline font-medium"
                  >
                    Terms of Use
                  </Link>{" "}
                  and acknowledge our{" "}
                  <Link
                    to="/privacy"
                    className="text-cyan-500 hover:text-cyan-600 underline font-medium"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
